import type { ImageRef } from "@/content/types";
import { MediaImage } from "@/components/common/MediaImage";

interface Props {
  image: ImageRef;
}

/**
 * Full-bleed soft backdrop for the release detail page. Fills the viewport
 * width, extends past the hero block, and fades into the page background
 * by ~90% height so content below reads cleanly. The MediaImage inside picks
 * AVIF/WebP variants automatically via <picture>.
 *
 * Mounts at the top of a `position: relative` parent and sits behind content
 * (z-index 0). Page content should set `position: relative` + a higher
 * z-index so it stacks over the backdrop.
 */
export function ReleaseBackdrop({ image }: Props) {
  return (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: "50%",
        transform: "translateX(-50%)",
        width: "100vw",
        height: "clamp(720px, 110vh, 1400px)",
        overflow: "hidden",
        zIndex: 0,
        pointerEvents: "none",
      }}
    >
      <MediaImage
        image={image}
        loading="eager"
        sizes="100vw"
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center top",
        }}
      />
      {/* Two-stop overlay:
          (1) a soft dim across the whole image so titles stay legible; and
          (2) a downward fade to the page bg color, hitting opaque before the
              image's natural edge so the seam never shows. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          // rgb(14,11,12) is --color-void — the actual html/body background
          // (see src/styles/global.css). Matching it explicitly here so the
          // fade lands on the same color the page sits on with no seam.
          // (Don't switch to --color-deeper; that's the cooler #0d0e1f used
          // for component backings and bleeds blue at the join.)
          // Top stop is heavier than first iteration — bright cover art
          // (e.g. STIMULATE) was competing with foreground text/buttons.
          background:
            "linear-gradient(180deg, rgba(14,11,12,0.65) 0%, rgba(14,11,12,0.8) 50%, #0e0b0c 88%)",
        }}
      />
    </div>
  );
}
